import Sidebar from '@/components/Sidebar'
import { cn } from '@/lib/utils'
import NoteListPage from '@/pages/primary/NoteListPage'
import HomePage from '@/pages/secondary/HomePage'
import ConversationListPage from '@/pages/primary/ConversationListPage'
import { TPageRef } from '@/types'
import React, {
  cloneElement,
  createContext,
  createRef,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { getDefaultStore } from 'jotai'
import BackgroundAudio from './components/BackgroundAudio'
import BottomNavigationBar from './components/BottomNavigationBar'
import { normalizeUrl, normalizeHttpUrlPublic } from './lib/url'
import { redirectLegacyHexPath } from './lib/link'
import NotificationListPage from './pages/primary/NotificationListPage'
import ProfilePage from './pages/primary/ProfilePage'
import ExternalContentPage from './pages/secondary/ExternalContentPage'
import SearchPage from './pages/primary/SearchPage'
import PendingPage from './pages/primary/PendingPage'
import DatabasePage from './pages/primary/DatabasePage'
import { useNotificationProcessor } from './atoms/notifications'
import { isSmallScreenAtom } from './atoms/screenSize'
import { atomWithStorage } from 'jotai/utils'
import { useAtom, useAtomValue } from 'jotai'
import { enableSingleColumnLayoutAtom } from '@/atoms/preferences'
import { routes } from './routes'
import { popModal } from './services/modal-manager.service'
import QuickSwitch from './components/QuickSwitch'
import {
  feedQueryParam,
  PRIMARY_QUERY_PARAMS,
  recentRelayFeedsAtom,
  storedFeedAtom,
  TFeed
} from './atoms/feed'
import { AddressPointer, decode, naddrEncode } from '@nostr/tools/nip19'
import { loadRelaySets, ResolvedSet } from '@nostr/gadgets/sets'
import { isHex32 } from '@nostr/gadgets/utils'

const store = getDefaultStore()
const MAX_RECENT_RELAY_FEEDS = 3
export const primaryStackTopIndex = { current: -1 }

export function setFeed(feed: TFeed) {
  const current = store.get(storedFeedAtom)
  if (current && JSON.stringify(current) === JSON.stringify(feed)) return
  store.set(storedFeedAtom, feed)
  if ('relay' in feed) trackRecentRelayFeed(feed.relay)
  else if ('relaySet' in feed) trackRecentRelayFeed(feed.relaySet)
}

export function pushFeedURL(feed: TFeed) {
  const param = feedQueryParam(feed)
  const url = new URL(window.location.href)
  for (const name of PRIMARY_QUERY_PARAMS) url.searchParams.delete(name)
  if (param) url.searchParams.set(param[0], param[1])
  const href = url.toString()
  if (href === window.location.href) return
  window.history.pushState(
    { feedSwitch: true, index: primaryStackTopIndex.current, url: href },
    '',
    href
  )
}

export function clearPrimaryQueryURL() {
  const url = new URL(window.location.href)
  for (const name of PRIMARY_QUERY_PARAMS) url.searchParams.delete(name)
  const href = url.toString()
  if (href === window.location.href) return
  window.history.replaceState({ ...window.history.state, url: href }, '', href)
}

export async function switchFeed(feed: TFeed) {
  pushFeedURL(feed)
  setFeed(feed)
}

export async function resolveRelaySetFeed(
  pointer: AddressPointer
): Promise<ResolvedSet<string> | null> {
  const result = await loadRelaySets(pointer.pubkey, pointer.relays ?? [])
  const set = result[pointer.identifier]
  if (!set || !set.items.length) return null
  return set
}

export function trackRecentRelayFeed(ref: string | ResolvedSet<string>) {
  store.set(recentRelayFeedsAtom, (current) => {
    const key = recentRelayKey(ref)
    return [ref, ...current.filter((item) => recentRelayKey(item) !== key)].slice(
      0,
      MAX_RECENT_RELAY_FEEDS
    )
  })
}

function recentRelayKey(ref: string | ResolvedSet<string>) {
  return typeof ref === 'string'
    ? `relay:${normalizeUrl(ref)}`
    : `set:${ref.pointer.pubkey}:${ref.pointer.identifier}`
}

export type TPrimaryPageName = keyof typeof PRIMARY_PAGE_MAP

type TPrimaryPageContext = {
  navigate: (page: TPrimaryPageName, props?: object) => void
  current: TPrimaryPageName | null
  display: boolean
}

type TSecondaryPageContext = {
  push: (url: string) => void
  pop: () => void
  currentIndex: number
}

type TStackItem = {
  index: number
  url: string
  component: React.ReactElement | null
  ref: RefObject<TPageRef> | null
}

const PRIMARY_PAGE_REF_MAP = {
  home: createRef<TPageRef>(),
  conversations: createRef<TPageRef>(),
  notifications: createRef<TPageRef>(),
  profile: createRef<TPageRef>(),
  search: createRef<TPageRef>(),
  pending: createRef<TPageRef>(),
  database: createRef<TPageRef>()
}

const PRIMARY_PAGE_MAP = {
  home: <NoteListPage ref={PRIMARY_PAGE_REF_MAP.home} />,
  conversations: <ConversationListPage ref={PRIMARY_PAGE_REF_MAP.conversations} />,
  notifications: <NotificationListPage ref={PRIMARY_PAGE_REF_MAP.notifications} />,
  profile: <ProfilePage ref={PRIMARY_PAGE_REF_MAP.profile} />,
  search: <SearchPage ref={PRIMARY_PAGE_REF_MAP.search} />,
  pending: <PendingPage ref={PRIMARY_PAGE_REF_MAP.pending} />,
  database: <DatabasePage ref={PRIMARY_PAGE_REF_MAP.database} />
}

const PrimaryPageContext = createContext<TPrimaryPageContext | undefined>(undefined)

const SecondaryPageContext = createContext<TSecondaryPageContext | undefined>(undefined)

const currentPrimaryPageAtom = atomWithStorage<TPrimaryPageName>(
  's:lastPrimaryPage',
  'home',
  undefined,
  {
    getOnInit: true
  }
)

export function usePrimaryPage() {
  const context = useContext(PrimaryPageContext)
  if (!context) {
    throw new Error('usePrimaryPage must be used within a PrimaryPageContext.Provider')
  }
  return context
}

export function useSecondaryPage() {
  const context = useContext(SecondaryPageContext)
  if (!context) {
    throw new Error('useSecondaryPage must be used within a SecondaryPageContext.Provider')
  }
  return context
}

function NotificationsProcessor() {
  useNotificationProcessor()
  return null
}

export function PageManager({ maxStackSize = 5 }: { maxStackSize?: number }) {
  const [currentPrimaryPage, setCurrentPrimaryPage] = useAtom(currentPrimaryPageAtom)
  const [primaryPages, setPrimaryPages] = useState<
    { name: TPrimaryPageName; element: ReactNode; props?: any }[]
  >(() => [
    {
      name: currentPrimaryPage in PRIMARY_PAGE_MAP ? currentPrimaryPage : 'home',
      element:
        PRIMARY_PAGE_MAP[currentPrimaryPage in PRIMARY_PAGE_MAP ? currentPrimaryPage : 'home']
    }
  ])
  const [secondaryStack, setSecondaryStack] = useState<TStackItem[]>([])
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const enableSingleColumnLayout = useAtomValue(enableSingleColumnLayoutAtom)
  const ignorePopStateRef = useRef(false)
  const [leftColumnWidth, setLeftColumnWidth] = useState(() => {
    const stored = localStorage.getItem('column-width')
    return stored ? parseFloat(stored) : 50
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    localStorage.setItem('column-width', leftColumnWidth.toString())
  }, [leftColumnWidth])

  useEffect(() => {
    primaryStackTopIndex.current = secondaryStack.length
      ? secondaryStack[secondaryStack.length - 1].index
      : -1
  }, [secondaryStack])

  useEffect(() => {
    handleRef.current?.addEventListener?.('mousedown', handleMouseDown)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      removeStyles()
    }

    function removeStyles() {
      const style = document.getElementById('resize-cursor-override')
      if (style) style.remove()
      if (containerRef.current?.style) {
        containerRef.current.style.userSelect = 'auto'
        containerRef.current.style.cursor = 'auto'
      }
    }

    function handleMouseDown() {
      document.addEventListener('mousemove', handleMouseMove)
      if (containerRef.current?.style) {
        containerRef.current.style.userSelect = 'none'
        containerRef.current.style.cursor = 'col-resize'
      }
    }

    function handleMouseMove(e: MouseEvent) {
      if (!containerRef.current) return
      const containerRect = containerRef.current.getBoundingClientRect()
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100
      const clampedWidth = Math.max(20, Math.min(80, newWidth))
      setLeftColumnWidth(clampedWidth)
    }

    function handleMouseUp() {
      document.removeEventListener('mousemove', handleMouseMove)
      removeStyles()
    }
  }, [])

  useEffect(() => {
    window.history.pushState(null, '', window.location.href)
    seedInitialFeedQuery()

    const initialRoute = getPrimaryRoute(window.location.href)
    if (initialRoute) applyPrimaryRoute(initialRoute)

    if (window.location.pathname !== '/') {
      const url = redirectLegacyHexPath(
        window.location.pathname + window.location.search + window.location.hash
      )
      setSecondaryStack((prevStack) => {
        if (isCurrentPage(prevStack, url)) return prevStack

        const { newStack, newItem } = pushNewPageToStack(
          prevStack,
          url,
          maxStackSize,
          window.history.state?.index
        )
        if (newItem) {
          window.history.replaceState({ index: newItem.index, url }, '', url)
        }
        return newStack
      })
    }

    window.addEventListener('popstate', onPopState)

    return () => {
      window.removeEventListener('popstate', onPopState)
    }

    function onPopState(e: PopStateEvent) {
      if (ignorePopStateRef.current) {
        ignorePopStateRef.current = false
        return
      }

      const closeModal = popModal()
      if (closeModal) {
        ignorePopStateRef.current = true
        window.history.forward()
        return
      }

      const route = getPrimaryRoute(window.location.href)
      if (route) {
        applyPrimaryRoute(route, false)
      } else if (!e.state && window.location.pathname === '/' && !window.location.search) {
        showPrimaryPage('home', undefined, false)
      }

      let state = e.state as { index: number; url: string } | null
      setSecondaryStack((pre) => {
        const currentItem = pre[pre.length - 1] as TStackItem | undefined
        const currentIndex = currentItem?.index
        if (!state) {
          if (window.location.pathname + window.location.search + window.location.hash !== '/') {
            const url = redirectLegacyHexPath(
              window.location.pathname + window.location.search + window.location.hash
            )
            const newIndex = currentIndex !== undefined ? currentIndex + 1 : 0
            const { newStack } = pushNewPageToStack(pre, url, maxStackSize, newIndex)
            return newStack
          } else {
            // Back to root
            state = { index: -1, url: '/' }
          }
        }

        // Go forward
        if (currentIndex === undefined || state.index > currentIndex) {
          const { newStack } = pushNewPageToStack(pre, state.url, maxStackSize)
          return newStack
        }

        if (state.index === currentIndex) {
          return pre
        }

        // Go back
        const newStack = pre.filter((item) => item.index <= state!.index)
        const topItem = newStack[newStack.length - 1] as TStackItem | undefined
        if (!topItem) {
          // Create a new stack item if it's not exist (e.g. when the user refreshes the page, the stack will be empty)
          const { component, ref } = findAndCreateComponent(state.url, state.index)
          if (component) {
            newStack.push({
              index: state.index,
              url: state.url,
              component,
              ref
            })
          }
        } else if (!topItem.component) {
          // Load the component if it's not cached
          const { component, ref } = findAndCreateComponent(topItem.url, state.index)
          if (component) {
            topItem.component = component
            topItem.ref = ref
          }
        }
        if (newStack.length === 0) {
          const url = new URL(window.location.href)
          url.pathname = '/'
          window.history.replaceState(null, '', url.toString())
        }
        return newStack
      })
    }
  }, [])

  const gridStyle = useMemo(
    () => ({ gridTemplateColumns: `${leftColumnWidth}% ${100 - leftColumnWidth}%` }),
    [leftColumnWidth]
  )

  const resizeHandleStyle = useMemo(() => ({ left: `${leftColumnWidth}%` }), [leftColumnWidth])

  if (isSmallScreen) {
    return (
      <PrimaryPageContext.Provider
        value={{
          navigate: navigatePrimaryPage,
          current: currentPrimaryPage,
          display: secondaryStack.length === 0
        }}
      >
        <NotificationsProcessor />
        <SecondaryPageContext.Provider
          value={{
            push: pushSecondaryPage,
            pop: popSecondaryPage,
            currentIndex: secondaryStack.length
              ? secondaryStack[secondaryStack.length - 1].index
              : 0
          }}
        >
          {!!secondaryStack.length &&
            secondaryStack.map((item, index) => (
              <div key={item.index} className={index === secondaryStack.length - 1 ? '' : 'hidden'}>
                {item.component}
              </div>
            ))}
          {primaryPages.map(({ name, element, props }) => (
            <div
              key={name}
              className={secondaryStack.length === 0 && currentPrimaryPage === name ? '' : 'hidden'}
            >
              {props ? cloneElement(element as React.ReactElement, props) : element}
            </div>
          ))}
          <BottomNavigationBar />
          <QuickSwitch />
        </SecondaryPageContext.Provider>
      </PrimaryPageContext.Provider>
    )
  }

  if (enableSingleColumnLayout) {
    return (
      <PrimaryPageContext.Provider
        value={{
          navigate: navigatePrimaryPage,
          current: currentPrimaryPage,
          display: secondaryStack.length === 0
        }}
      >
        <NotificationsProcessor />
        <SecondaryPageContext.Provider
          value={{
            push: pushSecondaryPage,
            pop: popSecondaryPage,
            currentIndex: secondaryStack.length
              ? secondaryStack[secondaryStack.length - 1].index
              : 0
          }}
        >
          <div className="flex lg:justify-around w-full">
            <div className="sticky top-0 lg:w-full flex justify-end self-start h-[var(--vh)]">
              <Sidebar />
            </div>
            <div className="flex-1 w-0 bg-background border-x lg:flex-auto lg:w-[640px] lg:shrink-0">
              {!!secondaryStack.length &&
                secondaryStack.map((item, index) => (
                  <div
                    key={item.index}
                    className={index === secondaryStack.length - 1 ? '' : 'hidden'}
                  >
                    {item.component}
                  </div>
                ))}
              {primaryPages.map(({ name, element, props }) => (
                <div
                  key={name}
                  className={
                    secondaryStack.length === 0 && currentPrimaryPage === name ? '' : 'hidden'
                  }
                >
                  {props ? cloneElement(element as React.ReactElement, props) : element}
                </div>
              ))}
            </div>
            <div className="hidden lg:w-full lg:block" />
          </div>
          <BackgroundAudio className="fixed bottom-20 right-0 z-50 w-80 rounded-l-full rounded-r-none overflow-hidden shadow-lg border" />
          <QuickSwitch />
        </SecondaryPageContext.Provider>
      </PrimaryPageContext.Provider>
    )
  }

  return (
    <PrimaryPageContext.Provider
      value={{
        navigate: navigatePrimaryPage,
        current: currentPrimaryPage,
        display: true
      }}
    >
      <NotificationsProcessor />
      <SecondaryPageContext.Provider
        value={{
          push: pushSecondaryPage,
          pop: popSecondaryPage,
          currentIndex: secondaryStack.length ? secondaryStack[secondaryStack.length - 1].index : 0
        }}
      >
        <div className="flex flex-col items-center bg-surface-background">
          <div className="flex h-[var(--vh)] w-full bg-surface-background max-w-[1920px]">
            <Sidebar />
            <div
              ref={containerRef}
              className={cn('relative grid w-full', 'gap-2 pr-2 py-2')}
              style={gridStyle}
            >
              <div className={cn('bg-background overflow-hidden', 'rounded-lg shadow-lg')}>
                {primaryPages.map(({ name, element, props }) => (
                  <div
                    key={name}
                    className={cn(
                      'flex flex-col h-full w-full',
                      currentPrimaryPage === name ? '' : 'hidden'
                    )}
                  >
                    {props ? cloneElement(element as React.ReactElement, props) : element}
                  </div>
                ))}
              </div>
              {/* Resize handle */}
              <div
                ref={handleRef}
                className="absolute top-0 bottom-0 w-1 hover:w-2 -ml-1 cursor-col-resize z-10 transition-all bg-transparent"
                style={resizeHandleStyle}
              />
              <div className={cn('bg-background overflow-hidden', 'rounded-lg shadow-lg')}>
                {secondaryStack.map((item, index) => (
                  <div
                    key={item.index}
                    className={cn(
                      'flex flex-col h-full w-full',
                      index === secondaryStack.length - 1 ? '' : 'hidden'
                    )}
                  >
                    {item.component}
                  </div>
                ))}
                <div
                  key="home"
                  className={cn(
                    'flex flex-col h-full w-full',
                    secondaryStack.length === 0 ? '' : 'hidden'
                  )}
                >
                  <HomePage />
                </div>
              </div>
            </div>
          </div>
        </div>
        <BackgroundAudio className="fixed bottom-20 right-0 z-50 w-80 rounded-l-full rounded-r-none overflow-hidden shadow-lg border" />
        <QuickSwitch />
      </SecondaryPageContext.Provider>
    </PrimaryPageContext.Provider>
  )

  function popSecondaryPage() {
    const current = secondaryStack[secondaryStack.length - 1]
    if (!current) return

    const previous = secondaryStack[secondaryStack.length - 2]
    const previousURL =
      previous?.url ??
      (() => {
        const url = new URL(window.location.href)
        url.pathname = '/'
        return url.toString()
      })()

    // Keep browser history aligned with PageManager while preserving forward navigation.
    window.history.replaceState(
      previous ? { index: previous.index, url: previous.url } : { index: -1, url: previousURL },
      '',
      previousURL
    )
    window.history.pushState({ index: current.index, url: current.url }, '', current.url)
    window.history.back()
  }

  function showPrimaryPage(page: TPrimaryPageName, props?: any, scrollToTopOnSamePage = true) {
    const needScrollToTop = scrollToTopOnSamePage && page === currentPrimaryPage

    setPrimaryPages((prev) => {
      const exists = prev.find((p) => p.name === page)
      if (exists && props) {
        exists.props = props
        return [...prev]
      } else if (!exists) {
        return [...prev, { name: page, element: PRIMARY_PAGE_MAP[page], props }]
      }
      return prev
    })
    setCurrentPrimaryPage(page)
    if (needScrollToTop) {
      PRIMARY_PAGE_REF_MAP[page].current?.scrollToTop('smooth')
    }
  }

  function navigatePrimaryPage(page: TPrimaryPageName, props?: any) {
    showPrimaryPage(page, props)

    if (page === 'conversations' || page === 'notifications') {
      clearPrimaryQueryURL()
    }

    const isSearch = page === 'search' && props?.input !== undefined

    if (enableSingleColumnLayout) {
      const url = new URL(window.location.href)
      url.pathname = '/'
      if (isSearch) {
        for (const name of PRIMARY_QUERY_PARAMS) url.searchParams.delete(name)
        url.searchParams.set('search', props.input)
      }
      const href = url.toString()
      if (href !== window.location.href) {
        window.history.pushState({ index: -1, url: href }, '', href)
      }
      if (secondaryStack.length > 0) {
        primaryStackTopIndex.current = -1
        setSecondaryStack([])
      }
      return
    }

    if (isSearch) {
      const url = new URL(window.location.href)
      for (const name of PRIMARY_QUERY_PARAMS) url.searchParams.delete(name)
      url.searchParams.set('search', props.input)
      const href = url.toString()
      if (href !== window.location.href) {
        window.history.pushState({ url: href }, '', href)
      }
    }
  }

  function pushSecondaryPage(url: string, index?: number) {
    url = redirectLegacyHexPath(url)

    let parsed: URL | null = null
    try {
      parsed = new URL(url, window.location.origin)
    } catch {
      /***/
    }
    if (parsed && parsed.pathname === '/') {
      const route = getPrimaryRoute(url)
      if (route) {
        pushPrimaryRouteURL(route)
        applyPrimaryRoute(route)
        if (enableSingleColumnLayout) {
          primaryStackTopIndex.current = -1
          setSecondaryStack([])
        }
        return
      }
    }

    url = appendPrimaryQuery(url)

    setSecondaryStack((prevStack) => {
      if (isCurrentPage(prevStack, url)) {
        const currentItem = prevStack[prevStack.length - 1]
        if (currentItem?.ref?.current) {
          currentItem.ref.current.scrollToTop('instant')
        }
        return prevStack
      }

      const { newStack, newItem } = pushNewPageToStack(prevStack, url, maxStackSize, index)
      if (newItem) {
        window.history.pushState({ index: newItem.index, url }, '', url)
        primaryStackTopIndex.current = newItem.index
      }
      return newStack
    })
  }

  function applyPrimaryRoute(route: PrimaryRoute, scrollToTopOnSamePage = true) {
    if (route.feed) {
      setFeed(route.feed)
      showPrimaryPage('home', undefined, scrollToTopOnSamePage)
    } else if (route.relaySetPointer) {
      resolveRelaySetFeed(route.relaySetPointer).then((set) => {
        if (!set) return
        setFeed({ relaySet: set })
        showPrimaryPage('home', undefined, scrollToTopOnSamePage)
      })
    } else if (route.searchInput !== undefined) {
      showPrimaryPage('search', { input: route.searchInput }, scrollToTopOnSamePage)
    }
  }

  function pushPrimaryRouteURL(route: PrimaryRoute) {
    if (route.feed) {
      pushFeedURL(route.feed)
      return
    }

    const url = new URL(window.location.href)
    for (const name of PRIMARY_QUERY_PARAMS) url.searchParams.delete(name)
    if (route.relaySetPointer) {
      url.searchParams.set('s', naddrEncode(route.relaySetPointer))
    } else if (route.searchInput !== undefined) {
      url.searchParams.set('search', route.searchInput)
    }
    const href = url.toString()
    if (href === window.location.href) return
    window.history.pushState(
      { feedSwitch: true, index: primaryStackTopIndex.current, url: href },
      '',
      href
    )
  }
}

export const SecondaryPageLink = React.forwardRef<
  HTMLAnchorElement,
  {
    to: string
    children: React.ReactNode
    className?: string
    onClick?: (e: React.MouseEvent) => void
  }
>(function SecondaryPageLink({ to, children, className, onClick }, ref) {
  const { push } = useSecondaryPage()

  return (
    <a
      ref={ref}
      href={to}
      className={cn('cursor-pointer', className)}
      onClick={(e) => {
        e.preventDefault()
        if (onClick) {
          onClick(e)
        }
        push(to)
      }}
    >
      {children}
    </a>
  )
})

function isCurrentPage(stack: TStackItem[], url: string) {
  const currentPage = stack[stack.length - 1]
  if (!currentPage) return false

  return currentPage.url === url
}

function findAndCreateComponent(url: string, index: number) {
  const path = url.split('?')[0].split('#')[0]

  const searchParams = new URLSearchParams(url.split('?')[1] || '')

  const externalContentUrl = searchParams.get('i')
  if (path === '/external-content' && externalContentUrl) {
    const normalized = normalizeHttpUrlPublic(externalContentUrl)
    if (normalized) {
      const ref = createRef<TPageRef>()
      return {
        component: cloneElement(<ExternalContentPage />, { url: normalized, index, ref } as any),
        ref
      }
    }
  }

  for (const { matcher, element } of routes) {
    const match = matcher(path)
    if (!match) continue

    if (!element) return {}
    const ref = createRef<TPageRef>()
    return { component: cloneElement(element, { ...match.params, index, ref } as any), ref }
  }
  return {}
}

type PrimaryRoute = {
  feed?: TFeed
  relaySetPointer?: AddressPointer
  searchInput?: string
}

function getPrimaryRoute(url: string): PrimaryRoute | undefined {
  let parsed: URL
  try {
    parsed = new URL(url, window.location.origin)
  } catch {
    return undefined
  }
  const searchParams = parsed.searchParams

  const relayParam = searchParams.get('r')
  if (relayParam) {
    const normalized = normalizeUrl(relayParam)
    if (normalized) return { feed: { relay: normalized } }
  }

  const pubkeyParam = searchParams.get('p')
  if (pubkeyParam) {
    if (isHex32(pubkeyParam)) return { feed: { pubkey: pubkeyParam } }
    try {
      const { type, data } = decode(pubkeyParam)
      if (type === 'npub') return { feed: { pubkey: data as string } }
      if (type === 'nprofile') return { feed: { pubkey: (data as { pubkey: string }).pubkey } }
    } catch {
      /***/
    }
  }

  const hardcodedParam = searchParams.get('h')
  if (hardcodedParam && /^\d+$/.test(hardcodedParam)) {
    const index = parseInt(hardcodedParam)
    const hardcodedFeeds = window.hallway.universe.hardcodedFeeds
    if (hardcodedFeeds && index >= 0 && index < hardcodedFeeds.length) {
      return { feed: { hardcodedFeed: index } }
    }
  }

  const relaySetParam = searchParams.get('s')
  if (relaySetParam) {
    try {
      const { type, data } = decode(relaySetParam)
      if (type === 'naddr') {
        const pointer = data as AddressPointer
        if (pointer.pubkey && pointer.identifier && pointer.kind === 30002) {
          return { relaySetPointer: pointer }
        }
      }
    } catch {
      /***/
    }
  }

  if (searchParams.has('search')) {
    return { searchInput: searchParams.get('search') || '' }
  }

  return undefined
}

function appendPrimaryQuery(url: string) {
  const current = new URL(window.location.href)
  const primaryParam = PRIMARY_QUERY_PARAMS.find((name) => current.searchParams.has(name))
  if (!primaryParam) return url

  try {
    const target = new URL(url, window.location.origin)
    if (PRIMARY_QUERY_PARAMS.some((name) => target.searchParams.has(name))) return url
    target.searchParams.set(primaryParam, current.searchParams.get(primaryParam)!)
    return target.pathname + target.search + target.hash
  } catch {
    return url
  }
}

function seedInitialFeedQuery() {
  const url = new URL(window.location.href)
  if (PRIMARY_QUERY_PARAMS.some((name) => url.searchParams.has(name))) return

  try {
    const raw = localStorage.getItem('s:feedInfo')
    if (!raw) return
    const feed = JSON.parse(raw) as TFeed
    const param = feedQueryParam(feed)
    if (!param) return
    url.searchParams.set(param[0], param[1])
    window.history.replaceState(window.history.state, '', url)
  } catch {
    /***/
  }
}

function pushNewPageToStack(
  stack: TStackItem[],
  url: string,
  maxStackSize = 5,
  specificIndex?: number
) {
  const currentItem = stack[stack.length - 1]
  const currentIndex = specificIndex ?? (currentItem ? currentItem.index + 1 : 0)

  const { component, ref } = findAndCreateComponent(url, currentIndex)
  if (!component) return { newStack: stack, newItem: null }

  const newItem = { component, ref, url, index: currentIndex }
  const newStack = [...stack, newItem]
  const lastCachedIndex = newStack.findIndex((stack) => stack.component)
  // Clear the oldest cached component if there are too many cached components
  if (newStack.length - lastCachedIndex > maxStackSize) {
    newStack[lastCachedIndex].component = null
  }
  return { newStack, newItem }
}
